import { normalizeNullableId } from '@/lib/services/occasions/occasions.utils'
import {
  applyMainBlockSortOrder,
  CONTACT_US_WIDGET_BLOCK_TYPE,
  CUSTOMER_REVIEWS_FORM_PLACEMENT_INLINE,
  CUSTOMER_REVIEWS_PAGE_BLOCK_TYPE,
  CUSTOMER_REVIEWS_WIDGET_BLOCK_TYPE,
  DEFAULT_CONTACT_US_WIDGET_BUTTON_TEXT,
  DEFAULT_CONTACT_US_WIDGET_MESSAGE,
  DEFAULT_CONTACT_US_WIDGET_TITLE,
  DEFAULT_REVIEWS_WIDGET_MESSAGE,
  DEFAULT_REVIEWS_WIDGET_TITLE,
  DEFAULT_SCHEDULE,
  DEFAULT_SECTION_OVERRIDES,
  FEATURED_COLLECTION_BLOCK_TYPE,
  FEATURED_PRODUCT_BLOCK_TYPE,
  GALLERY_WIDGET_BLOCK_TYPE,
  getPreferredSeasonalFeaturedCollectionBlock,
  getSeasonalReferenceHeroBlock,
  isFeaturedContentWidgetBlock,
  NO_OCCASION_OPTION_ID,
  normalizeCustomerReviewsFormPlacement,
  normalizeSchedule,
  normalizeSectionOverrides,
} from './designForm.utils'
import {
  cornerStyleOptions,
  DEFAULT_CORNER_STYLE,
  DEFAULT_FONT_PAIRING,
  DEFAULT_HERO_LAYOUT,
  fontPairingOptions,
  heroLayoutOptions,
  heroLayoutOptionsWithInherited,
  resolveOptionId,
} from './storefrontStyleOptions'
import { normalizeHeroValuesForEditing } from './heroValues'

const DEFAULT_PRODUCT_DISCLAIMER =
  'We strive to fulfill your order as pictured; however, substitutions may be necessary due to flower availability. When substitutions are made, we match color and size as closely as possible and always use blooms of equal or greater value.'
const PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_ALL = 'all'
const PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_INCLUDE = 'include'

export const createBaseDesignValues = () => ({
  name: '',
  status: 'published',
  is_default: false,
  occasion_id: NO_OCCASION_OPTION_ID,
  seasonal_tag_id: NO_OCCASION_OPTION_ID,
  section_overrides: { ...DEFAULT_SECTION_OVERRIDES },
  seasonal: {
    seasonal_tag_id: null,
    seasonal_design_id: null,
    hero_mode: 'reference',
    keep_for_future_years: false,
    selected_year: new Date().getFullYear(),
    florist_reviewed_at: null,
    featured_collection_id: null,
    featured_collection_mode: 'automatic',
    featured_style_mode: 'inherit',
    featured_style: null,
    navigation_mode: 'automatic',
  },
  schedule: { ...DEFAULT_SCHEDULE },
  pages: {
    '/': {
      title: 'Home',
      slug: '/',
      blocks: {
        header: {
          banner: [{ is_enabled: true, props: { layout: 'split' }, nav_items: [] }],
          brand: [{ is_enabled: true, props: { logoMaxHeightPx: 64 } }],
          navMenu: [{ is_enabled: true, props: { itemsMode: 'automatic' }, nav_items: [] }],
        },
        hero: {
          hero: [
            {
              is_enabled: true,
              props: {
                layout: DEFAULT_HERO_LAYOUT,
                title: '',
                subtext: '',
                buttonText: 'Shop Flowers',
                buttonHref: '/products',
                imageFocalPointX: 50,
                imageFocalPointY: 50,
                imageAltText: '',
                preferredTextPosition: 'center',
                preferredTextTone: 'auto',
              },
              media: [],
            },
          ],
        },
        main: [],
        footer: {
          visitOurShop: [{ props: { showHours: true, showPhone: true, showAddress: true } }],
          footer: [{ props: { logoMaxHeightPx: 64 } }],
        },
      },
    },
    '/about-us': {
      title: 'About Us',
      slug: '/about-us',
      blocks: {
        main: [
          {
            type: 'aboutUsIntro',
            is_enabled: true,
            props: { title: '', text: '' },
            media: [],
          },
        ],
      },
    },
    '/products': {
      title: 'Products',
      slug: '/products',
      blocks: {
        main: [
          {
            type: 'productSettings',
            is_enabled: true,
            props: {
              disclaimerText: DEFAULT_PRODUCT_DISCLAIMER,
              productTypeScope: PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_ALL,
              includedProductTypeKeys: [],
            },
          },
        ],
      },
    },
    '/gallery': {
      title: 'Gallery',
      slug: '/gallery',
      blocks: {
        main: [
          {
            type: 'gallery',
            is_enabled: true,
            props: { title: 'Gallery', description: '' },
          },
          {
            type: 'album',
            is_enabled: true,
            props: {},
            albums: [],
          },
        ],
      },
    },
    '/contact-us': {
      title: 'Contact Us',
      slug: '/contact-us',
      blocks: {
        main: [
          {
            type: 'contactUsForm',
            is_enabled: true,
            props: {
              title: 'Contact Us',
              description:
                'Tell us how we can help. If this is for an event, share those details and we will follow up with next steps.',
            },
          },
        ],
      },
    },
    '/reviews': {
      title: 'Reviews',
      slug: '/reviews',
      blocks: {
        main: [
          {
            type: CUSTOMER_REVIEWS_PAGE_BLOCK_TYPE,
            is_enabled: true,
            props: {
              title: 'Reviews',
              description:
                'Read what customers are saying, or tell us how your flowers turned out.',
              formPlacement: CUSTOMER_REVIEWS_FORM_PLACEMENT_INLINE,
            },
          },
        ],
      },
    },
  },
})

const normalizeProductTypeKeys = (value) => {
  if (!Array.isArray(value)) {
    return []
  }

  return [
    ...new Set(
      value.map((key) => (typeof key === 'string' ? key.trim() : '')).filter((key) => key !== '')
    ),
  ]
}

const normalizeProductDisclaimerProps = (props = {}) => {
  const source = props && typeof props === 'object' ? props : {}
  const includedProductTypeKeys = normalizeProductTypeKeys(source.includedProductTypeKeys)
  const hasProductTypeScope =
    Object.prototype.hasOwnProperty.call(source, 'productTypeScope') &&
    source.productTypeScope != null

  if (hasProductTypeScope) {
    const productTypeScope =
      source.productTypeScope === PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_INCLUDE
        ? PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_INCLUDE
        : PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_ALL

    return {
      disclaimerText: source.disclaimerText ?? DEFAULT_PRODUCT_DISCLAIMER,
      productTypeScope,
      includedProductTypeKeys:
        productTypeScope === PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_INCLUDE
          ? includedProductTypeKeys
          : [],
    }
  }

  if (includedProductTypeKeys.length > 0) {
    return {
      disclaimerText: source.disclaimerText ?? DEFAULT_PRODUCT_DISCLAIMER,
      productTypeScope: PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_INCLUDE,
      includedProductTypeKeys,
    }
  }

  return {
    disclaimerText: source.disclaimerText ?? DEFAULT_PRODUCT_DISCLAIMER,
    productTypeScope: PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_ALL,
    includedProductTypeKeys: [],
  }
}

const getProductReferenceId = (productRef) => {
  const productId = Number(productRef?.product_id ?? productRef?.product?.id)

  return Number.isFinite(productId) && productId > 0 ? productId : null
}

const normalizeFeaturedProductReferences = (products) => {
  if (!Array.isArray(products) || products.length === 0) {
    return [{ product_id: null }]
  }

  return products.map((productRef) => ({
    ...productRef,
    product_id: getProductReferenceId(productRef),
  }))
}

const normalizeFeaturedContentWidgetBlock = (block) => {
  if (!isFeaturedContentWidgetBlock(block)) {
    return block
  }

  if (block?.type === FEATURED_COLLECTION_BLOCK_TYPE) {
    return {
      ...block,
      is_enabled: block?.is_enabled ?? true,
      props: {
        layout: block?.props?.layout ?? 'carousel',
        size: block?.props?.size ?? 'sm',
        imageAlign: block?.props?.imageAlign ?? 'left',
        title: block?.props?.title ?? '',
        ctaText: block?.props?.ctaText ?? '',
        ctaButtonText: block?.props?.ctaButtonText ?? '',
      },
      collections: Array.isArray(block?.collections)
        ? block.collections
        : [{ collection_id: null }],
    }
  }

  if (block?.type === FEATURED_PRODUCT_BLOCK_TYPE) {
    return {
      ...block,
      is_enabled: block?.is_enabled ?? true,
      props: {
        layout: 'split',
        imageAlign: block?.props?.imageAlign ?? 'left',
        title: block?.props?.title ?? '',
        ctaText: block?.props?.ctaText ?? '',
        ctaButtonText: block?.props?.ctaButtonText ?? '',
      },
      products: normalizeFeaturedProductReferences(block?.products),
    }
  }

  if (block?.type === GALLERY_WIDGET_BLOCK_TYPE) {
    return {
      ...block,
      is_enabled: block?.is_enabled ?? true,
      props: {
        layout: block?.props?.layout ?? 'carousel',
        size: block?.props?.size ?? 'sm',
        imageAlign: block?.props?.imageAlign ?? 'left',
        focusAlbumId: block?.props?.focusAlbumId ?? null,
      },
    }
  }

  if (block?.type === CONTACT_US_WIDGET_BLOCK_TYPE) {
    return {
      ...block,
      is_enabled: block?.is_enabled ?? true,
      props: {
        title: block?.props?.title ?? DEFAULT_CONTACT_US_WIDGET_TITLE,
        ctaText: block?.props?.ctaText ?? DEFAULT_CONTACT_US_WIDGET_MESSAGE,
        ctaButtonText: block?.props?.ctaButtonText ?? DEFAULT_CONTACT_US_WIDGET_BUTTON_TEXT,
      },
    }
  }

  if (block?.type === CUSTOMER_REVIEWS_WIDGET_BLOCK_TYPE) {
    return {
      ...block,
      is_enabled: block?.is_enabled ?? true,
      props: {
        title: block?.props?.title ?? DEFAULT_REVIEWS_WIDGET_TITLE,
        ctaText: block?.props?.ctaText ?? DEFAULT_REVIEWS_WIDGET_MESSAGE,
      },
    }
  }

  return {
    ...block,
    is_enabled: block?.is_enabled ?? true,
    props: block?.props && typeof block.props === 'object' ? block.props : {},
  }
}

const normalizeFeaturedContentWidgets = (
  blocks,
  { allowGalleryWidget = true, allowContactUsWidget = true, allowReviewsWidget = true } = {}
) =>
  (Array.isArray(blocks) ? blocks : [])
    .filter(isFeaturedContentWidgetBlock)
    .filter((block) => allowGalleryWidget || block?.type !== GALLERY_WIDGET_BLOCK_TYPE)
    .filter((block) => allowContactUsWidget || block?.type !== CONTACT_US_WIDGET_BLOCK_TYPE)
    .filter((block) => allowReviewsWidget || block?.type !== CUSTOMER_REVIEWS_WIDGET_BLOCK_TYPE)
    .map(normalizeFeaturedContentWidgetBlock)

const normalizeHomeMainBlock = (block) =>
  isFeaturedContentWidgetBlock(block) ? normalizeFeaturedContentWidgetBlock(block) : block

const normalizeHomeMainBlocksForForm = ({ blocks, isDefault }) => {
  const normalizedBlocks = applyMainBlockSortOrder(blocks.map(normalizeHomeMainBlock))

  if (isDefault) {
    return normalizedBlocks
  }

  const seasonalFeaturedCollection = getPreferredSeasonalFeaturedCollectionBlock(normalizedBlocks)

  return seasonalFeaturedCollection ? applyMainBlockSortOrder([seasonalFeaturedCollection]) : []
}

const createSeedDesign = (initialData, templateData) => {
  if (initialData) {
    return initialData
  }

  if (templateData) {
    return {
      ...templateData,
      id: undefined,
      created_from: undefined,
      name: '',
      status: 'published',
      is_default: false,
      schedule: null,
      section_overrides: { ...DEFAULT_SECTION_OVERRIDES },
    }
  }

  return createBaseDesignValues()
}

export const getHomepageHeroLayout = (design) =>
  design?.pages?.['/']?.blocks?.hero?.hero?.[0]?.props?.layout ?? null

export const normalizeDesignValues = (initialData = null, templateData = null, options = {}) => {
  const {
    allowInheritedHeroLayout = false,
    defaultHeroLayout = null,
    initializeSeasonalAutomation = false,
  } = options
  const design = createSeedDesign(initialData, templateData)
  const pages = { ...(design.pages ?? {}) }
  const normalizedSectionOverrides = normalizeSectionOverrides(design)
  const heroLayoutSelectOptions = allowInheritedHeroLayout
    ? heroLayoutOptionsWithInherited
    : heroLayoutOptions
  const occasionId = normalizeNullableId(design?.occasion_id ?? design?.occasion?.id)
  const seasonalTagId = normalizeNullableId(
    design?.seasonal?.seasonal_tag_id ??
      design?.seasonal?.occasion_id ??
      design?.seasonal_tag_id ??
      (initializeSeasonalAutomation ? occasionId : null)
  )
  const seasonalNavigationMode =
    design?.seasonal?.navigation_mode ??
    (normalizedSectionOverrides.header_nav_menu ? 'custom' : 'automatic')

  const homePage = pages['/'] ?? {}
  const homeBlocks = homePage?.blocks ?? {}
  const homeMainBlocks = Array.isArray(homeBlocks?.main) ? homeBlocks.main : []
  const header = homeBlocks?.header ?? {}
  const hero = homeBlocks?.hero ?? {}
  const footer = homeBlocks?.footer ?? {}

  const bannerBlock = Array.isArray(header?.banner) ? header.banner[0] || {} : {}
  const brandBlock = Array.isArray(header?.brand) ? header.brand[0] || {} : {}
  const navMenuBlock = Array.isArray(header?.navMenu) ? header.navMenu[0] || {} : {}
  const navigationItemsMode = design?.is_default
    ? (navMenuBlock?.props?.itemsMode ?? 'automatic')
    : seasonalNavigationMode
  const storedHeroBlock = Array.isArray(hero?.hero) ? hero.hero[0] || {} : {}
  const heroBlock = getSeasonalReferenceHeroBlock(design, storedHeroBlock)
  const normalizedHeroProps = normalizeHeroValuesForEditing(
    {
      ...(heroBlock?.props ?? {}),
      ...(defaultHeroLayout ? { layout: defaultHeroLayout } : {}),
    },
    {
      defaultLayout: DEFAULT_HERO_LAYOUT,
      layoutOptions: heroLayoutSelectOptions,
    }
  )

  const aboutUsPage = pages['/about-us'] ?? {}
  const aboutUsMainBlocks = Array.isArray(aboutUsPage?.blocks?.main) ? aboutUsPage.blocks.main : []
  const aboutUsBlock = aboutUsMainBlocks[0] ?? {}

  const productsPage = pages['/products'] ?? {}
  const productsMainBlocks = Array.isArray(productsPage?.blocks?.main)
    ? productsPage.blocks.main
    : []
  const productsBlock = productsMainBlocks[0] ?? {}
  const galleryPage = pages['/gallery'] ?? {}
  const galleryMainBlocks = Array.isArray(galleryPage?.blocks?.main) ? galleryPage.blocks.main : []
  const galleryBlock = galleryMainBlocks.find((block) => block?.type === 'gallery') ?? {}
  const galleryAlbumsBlock = galleryMainBlocks.find((block) => block?.type === 'album') ?? {}
  const contactUsPage = pages['/contact-us'] ?? {}
  const contactUsMainBlocks = Array.isArray(contactUsPage?.blocks?.main)
    ? contactUsPage.blocks.main
    : []
  const contactUsFormBlock =
    contactUsMainBlocks.find((block) => block?.type === 'contactUsForm') ?? {}
  const reviewsPage = pages['/reviews'] ?? {}
  const reviewsMainBlocks = Array.isArray(reviewsPage?.blocks?.main) ? reviewsPage.blocks.main : []
  const customerReviewsPageBlock =
    reviewsMainBlocks.find((block) => block?.type === CUSTOMER_REVIEWS_PAGE_BLOCK_TYPE) ?? {}
  const normalizedGalleryAlbums = Array.isArray(galleryAlbumsBlock?.albums)
    ? galleryAlbumsBlock.albums
        .map((entry, index) => {
          // Design save expects albums.*.id to be the actual album ID (not block_albums row ID).
          const id = Number(entry?.album_id ?? entry?.album?.id ?? entry?.id)
          if (!Number.isFinite(id)) return null

          return {
            id,
            sort_order: Number(entry?.sort_order) > 0 ? Number(entry.sort_order) : index + 1,
            album: entry?.album
              ? {
                  id,
                  name: entry.album.name ?? '',
                  description: entry.album.description ?? '',
                  media_count: Number(entry.album.media_count ?? entry.album?.media?.length ?? 0),
                }
              : null,
          }
        })
        .filter(Boolean)
    : []

  pages['/'] = {
    ...homePage,
    title: homePage.title ?? 'Home',
    slug: homePage.slug ?? '/',
    blocks: {
      ...homeBlocks,
      header: {
        ...header,
        banner: [
          {
            ...bannerBlock,
            is_enabled: bannerBlock.is_enabled ?? true,
            props: {
              layout: bannerBlock?.props?.layout ?? 'split',
            },
            nav_items: Array.isArray(bannerBlock?.nav_items) ? bannerBlock.nav_items : [],
          },
        ],
        brand: [
          {
            ...brandBlock,
            is_enabled: true,
            props: {
              logoMaxHeightPx: brandBlock?.props?.logoMaxHeightPx ?? 64,
            },
          },
        ],
        navMenu: [
          {
            ...navMenuBlock,
            is_enabled: navMenuBlock.is_enabled ?? true,
            props: {
              ...(navMenuBlock?.props ?? {}),
              itemsMode: navigationItemsMode,
            },
            nav_items: Array.isArray(navMenuBlock?.nav_items) ? navMenuBlock.nav_items : [],
          },
        ],
      },
      hero: {
        ...hero,
        hero: [
          {
            ...heroBlock,
            is_enabled: heroBlock.is_enabled ?? true,
            props: {
              ...normalizedHeroProps,
              buttonHref: heroBlock?.props?.buttonHref ?? '/products',
            },
            media: Array.isArray(heroBlock?.media) ? heroBlock.media : [],
          },
        ],
      },
      main: normalizeHomeMainBlocksForForm({
        blocks: homeMainBlocks,
        isDefault: !!design?.is_default,
      }),
      footer: {
        ...footer,
        visitOurShop: Array.isArray(footer?.visitOurShop)
          ? footer.visitOurShop
          : [
              {
                props: {
                  showHours: true,
                  showPhone: true,
                  showAddress: true,
                  showDeliveryZipCodes: true,
                },
              },
            ],
        footer: [
          {
            ...(footer?.footer?.[0] ?? {}),
            props: {
              ...(footer?.footer?.[0]?.props ?? {}),
              logoMaxHeightPx: footer?.footer?.[0]?.props?.logoMaxHeightPx ?? 64,
            },
          },
        ],
      },
    },
  }

  pages['/about-us'] = {
    ...aboutUsPage,
    title: aboutUsPage.title ?? 'About Us',
    slug: aboutUsPage.slug ?? '/about-us',
    blocks: {
      ...(aboutUsPage.blocks ?? {}),
      main: [
        {
          ...aboutUsBlock,
          type: 'aboutUsIntro',
          is_enabled: aboutUsBlock.is_enabled ?? true,
          props: {
            title: aboutUsBlock?.props?.title ?? '',
            text: aboutUsBlock?.props?.text ?? '',
          },
          media: Array.isArray(aboutUsBlock?.media) ? aboutUsBlock.media : [],
        },
        ...normalizeFeaturedContentWidgets(aboutUsMainBlocks),
      ],
    },
  }

  pages['/products'] = {
    ...productsPage,
    title: productsPage.title ?? 'Products',
    slug: productsPage.slug ?? '/products',
    blocks: {
      ...(productsPage.blocks ?? {}),
      main: [
        {
          ...productsBlock,
          type: 'productSettings',
          is_enabled: productsBlock.is_enabled ?? true,
          props: normalizeProductDisclaimerProps(productsBlock?.props),
        },
      ],
    },
  }

  pages['/gallery'] = {
    ...galleryPage,
    title: galleryPage.title ?? 'Gallery',
    slug: galleryPage.slug ?? '/gallery',
    blocks: {
      ...(galleryPage.blocks ?? {}),
      main: [
        {
          ...galleryBlock,
          type: 'gallery',
          is_enabled: true,
          props: {
            title: galleryBlock?.props?.title ?? 'Gallery',
            description: galleryBlock?.props?.description ?? '',
          },
        },
        {
          ...galleryAlbumsBlock,
          type: 'album',
          is_enabled: true,
          props: {
            ...(galleryAlbumsBlock?.props ?? {}),
          },
          albums: normalizedGalleryAlbums,
        },
        ...normalizeFeaturedContentWidgets(galleryMainBlocks, {
          allowGalleryWidget: false,
        }),
      ],
    },
  }

  pages['/contact-us'] = {
    ...contactUsPage,
    title: contactUsPage.title ?? 'Contact Us',
    slug: contactUsPage.slug ?? '/contact-us',
    blocks: {
      ...(contactUsPage.blocks ?? {}),
      main: [
        {
          ...contactUsFormBlock,
          type: 'contactUsForm',
          is_enabled: true,
          props: {
            title: contactUsFormBlock?.props?.title ?? 'Contact Us',
            description:
              contactUsFormBlock?.props?.description ??
              'Tell us how we can help. If this is for an event, share those details and we will follow up with next steps.',
          },
        },
        ...normalizeFeaturedContentWidgets(contactUsMainBlocks, {
          allowContactUsWidget: false,
        }),
      ],
    },
  }

  pages['/reviews'] = {
    ...reviewsPage,
    title: reviewsPage.title ?? 'Reviews',
    slug: reviewsPage.slug ?? '/reviews',
    blocks: {
      ...(reviewsPage.blocks ?? {}),
      main: [
        {
          ...customerReviewsPageBlock,
          type: CUSTOMER_REVIEWS_PAGE_BLOCK_TYPE,
          is_enabled: true,
          props: {
            title: customerReviewsPageBlock?.props?.title ?? 'Reviews',
            description:
              customerReviewsPageBlock?.props?.description ??
              'Read what customers are saying, or tell us how your flowers turned out.',
            formPlacement: normalizeCustomerReviewsFormPlacement(
              customerReviewsPageBlock?.props?.formPlacement
            ),
          },
        },
        ...normalizeFeaturedContentWidgets(reviewsMainBlocks, {
          allowReviewsWidget: false,
        }),
      ],
    },
  }

  return {
    ...design,
    occasion_id: occasionId ?? NO_OCCASION_OPTION_ID,
    seasonal_tag_id: seasonalTagId ?? NO_OCCASION_OPTION_ID,
    brand: design?.brand
      ? {
          ...design.brand,
          corner_style: resolveOptionId(
            design.brand.corner_style,
            cornerStyleOptions,
            DEFAULT_CORNER_STYLE
          ),
          font_pairing: resolveOptionId(
            design.brand.font_pairing,
            fontPairingOptions,
            DEFAULT_FONT_PAIRING
          ),
        }
      : design?.brand,
    pages,
    section_overrides: normalizedSectionOverrides,
    seasonal:
      design?.seasonal || initializeSeasonalAutomation
        ? {
            seasonal_tag_id: seasonalTagId,
            seasonal_design_id: design?.seasonal?.seasonal_design_id ?? null,
            seasonal_design: design?.seasonal?.seasonal_design ?? null,
            hero_mode: design?.seasonal?.hero_mode ?? 'reference',
            keep_for_future_years: !!design?.seasonal?.keep_for_future_years,
            selected_year: design?.seasonal?.selected_year ?? new Date().getFullYear(),
            florist_reviewed_at: design?.seasonal?.florist_reviewed_at ?? null,
            featured_collection_id: design?.seasonal?.featured_collection_id ?? null,
            effective_featured_collection_id:
              design?.seasonal?.effective_featured_collection_id ?? null,
            featured_collection_mode: design?.seasonal?.featured_collection_mode ?? 'automatic',
            featured_style_mode: design?.seasonal?.featured_style_mode ?? 'inherit',
            featured_style: design?.seasonal?.featured_style ?? null,
            navigation_mode: seasonalNavigationMode,
          }
        : design?.seasonal,
    schedule: normalizeSchedule(design?.schedule),
  }
}

export const getSavedDesignValues = ({ response, submittedValue, normalizationOptions = {} }) => {
  const responseDesign =
    response && typeof response === 'object' && response.pages ? response : null

  return normalizeDesignValues(responseDesign ?? submittedValue, null, normalizationOptions)
}
